import {
  Outlet,
  createFileRoute,
  useNavigate,
  createLink,
} from "@tanstack/react-router";
import { TanStackRouterDevtools } from "@tanstack/react-router-devtools";
import { Button, Popover, Separator, Spinner } from "@heroui/react";
import { useEffect, useState } from "react";
import { Icon } from "@iconify/react";

import { authClient } from "@/shared/auth";

const Link = createLink(Button);

export const Route = createFileRoute("/dashboard")({
  component: Router,
});

const SingOutButton = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const signOut = () => {
    setIsLoading(true);
    authClient
      .signOut()
      .then(() => navigate({ to: "/" }))
      .finally(() => setIsLoading(false));
  };

  return (
    <Button isPending={isLoading} variant="outline" onPress={signOut}>
      Выйти
    </Button>
  );
};

function Router() {
  const navigate = useNavigate();
  const { data, isPending, isRefetching } = authClient.useSession();

  const isAdmin = (data && data.user.role === "admin") ?? false;

  useEffect(() => {
    if (!data && !isPending && !isRefetching) navigate({ to: "/login" });
  }, [data, isPending, isRefetching]);

  if (isPending) {
    return (
      <div className="h-screen w-full flex flex-col justify-center items-center gap-y-4">
        <Spinner />
      </div>
    );
  }

  if (data && data.user.role !== "admin" && data.user.role !== "operator") {
    return (
      <div className="h-screen w-full flex flex-col justify-center items-center gap-y-4">
        <p>Вы не администратор</p>
        <SingOutButton />
      </div>
    );
  }

  return (
    <>
      <header className="border-b border-divider">
        <div className="container max-w-7xl mx-auto flex items-center gap-4 py-3 px-4">
          <Link className="font-bold text-xl" to="/dashboard" variant="ghost">
            Sapsano
          </Link>

          <nav className="hidden sm:flex items-center gap-2 flex-1">
            <Link to="/dashboard" variant="ghost">
              Главная
            </Link>

            <Popover>
              <Popover.Trigger>
                <Button variant="ghost">
                  Заказы
                  <Icon className="size-5" icon="ion:chevron-down" />
                </Button>
              </Popover.Trigger>

              <Popover.Content>
                <Popover.Dialog className="flex flex-col gap-1.5 p-1.5 w-48">
                  <Link fullWidth to="/dashboard/orders" variant="ghost">
                    Все заказы
                  </Link>

                  <Separator />

                  <Link fullWidth to="/dashboard/assign" variant="ghost">
                    Назначение на курьера
                  </Link>

                  <Separator />

                  <Link
                      fullWidth
                      to="/dashboard/courier_packages_accept"
                      variant="ghost"
                  >
                    Приемка у курьера
                  </Link>

                  <Separator />

                  <Link
                      fullWidth
                      to="/dashboard/order/accumulators"
                      variant="ghost"
                  >
                    Манифесты (Отгрузка)
                  </Link>

                  <Separator />

                  <Link
                      fullWidth
                      to="/dashboard/couriers-map"
                      variant="ghost"
                  >
                    Карта курьеров
                  </Link>

                  <Separator />

                  <Link fullWidth to="/dashboard/focus" variant="ghost">
                    ТСД логи
                  </Link>
                </Popover.Dialog>
              </Popover.Content>
            </Popover>

            {isAdmin && (
              <Link
                search={{ period: "today" }}
                to="/dashboard/statistics"
                variant="ghost"
              >
                Статистика
              </Link>
            )}

            {isAdmin && (
              <Link to="/dashboard/users" variant="ghost">
                Пользователи
              </Link>
            )}

            {isAdmin && (
              <Popover>
                <Popover.Trigger>
                  <Button variant="ghost">
                    Системные
                    <Icon className="size-5" icon="ion:chevron-down" />
                  </Button>
                </Popover.Trigger>
                <Popover.Content>
                  <Popover.Dialog className="flex flex-col gap-1.5 p-1.5 w-48">
                    <Link fullWidth to="/dashboard/tariffs" variant="ghost">
                      Все тарифы
                    </Link>

                    <Separator />

                    <Link
                      fullWidth
                      to="/dashboard/tariff/configuration"
                      variant="ghost"
                    >
                      Базовые цены
                    </Link>

                    <Separator />

                    <Link
                      fullWidth
                      to="/dashboard/package-options"
                      variant="ghost"
                    >
                      Опции упаковки
                    </Link>

                    <Separator />

                    <Link fullWidth to="/dashboard/cities" variant="ghost">
                      Города
                    </Link>

                    <Separator />

                    <Link fullWidth to="/dashboard/directions" variant="ghost">
                      Направления
                    </Link>

                    <Separator />

                    <Link fullWidth to="/dashboard/banners" variant="ghost">
                      Баннеры
                    </Link>

                    <Separator />

                    <Link
                      fullWidth
                      to="/dashboard/pickup-schedule"
                      variant="ghost"
                    >
                      Расписание забора
                    </Link>

                    <Separator />

                    <Link fullWidth to="/dashboard/documents" variant="ghost">
                      Документы
                    </Link>

                    <Separator />

                    <Link fullWidth to="/dashboard/contacts" variant="ghost">
                      Контакты
                    </Link>

                    <Separator />

                    <Link
                      fullWidth
                      to="/dashboard/app-versions"
                      variant="ghost"
                    >
                      Версии приложений
                    </Link>

                    <Separator />

                    <Link fullWidth to="/dashboard/black-list" variant="ghost">
                      Чёрный список SMS
                    </Link>
                  </Popover.Dialog>
                </Popover.Content>
              </Popover>
            )}
          </nav>

          <div className="flex items-center gap-2 ml-auto">
            <Link to="/dashboard/profile" variant="ghost">
              Профиль
            </Link>
          </div>
        </div>
      </header>

      <div className="container max-w-7xl mx-auto py-4">
        <Outlet />
      </div>

      <TanStackRouterDevtools />
    </>
  );
}
